import { useEffect, useRef } from 'react'
import { Box, useTheme } from '@mui/material'
import '@mdxeditor/editor/style.css'
import {
  MDXEditor,
  BoldItalicUnderlineToggles,
  ListsToggle,
  listsPlugin,
  toolbarPlugin,
  defaultSvgIcons,
  ButtonWithTooltip
} from '@mdxeditor/editor'
import RowStack from '../container/RowStack'
import colors from 'styles/colors'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import ChecklistIcon from '@mui/icons-material/Checklist'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'

const MdxEditor = ({
  value,
  changeCallback,
  readOnly,
  hideCopy = false,
  removePlaceholderPaddingX = true,
  setEditorRef = () => {},
  ...otherProps
}) => {
  const theme = useTheme()
  const mdxEditorRef = useRef(null)

  useEffect(() => {
    if (mdxEditorRef?.current) {
      setEditorRef(mdxEditorRef.current)
    }
    return () => {
      if (setEditorRef) {
        setEditorRef(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mdxEditorRef])

  const handleChange = updated => {
    if (updated !== value) {
      changeCallback(updated)
    }
  }

  const handleCopy = () => {
    navigator.clipboard
      .writeText(value)
      .then(() => {})
      .catch(err => console.error('Failed to copy text: ', err))
  }

  const containerSx = {
    height: '100%',
    '& .mdxeditor': {
      '.mdxeditor-toolbar': {
        padding: 4,
        borderTop: `1px solid ${colors.Grey100}`,
        borderBottom: `1px solid ${colors.Grey100}`,
        borderRadius: 0,
        backgroundColor: colors.White,
        '.MuiSvgIcon-root': {
          fontSize: 20,
          color: !readOnly ? theme.palette.primary.main : colors.Grey350
        },
        '.copyContent': {
          color: theme.palette.primary.main,
          pointerEvents: 'all'
        }
      },
      "li[role='checkbox']": {
        lineHeight: 'normal'
      }
    },
    '& .mdxContent': {
      paddingY: 2,
      textWrap: 'auto',
      ...(value
        ? theme.typography.body1
        : {
            ...theme.typography.body1KindaSmall,
            color: colors.Grey250,
            paddingX: removePlaceholderPaddingX ? 0 : 8
          })
    }
  }

  const customFormatIcons = {
    format_bold: <FormatBoldIcon />,
    format_italic: <FormatItalicIcon />,
    format_list_checked: <ChecklistIcon />,
    format_list_bulleted: <FormatListBulletedIcon />,
    format_list_numbered: <FormatListNumberedIcon />
  }

  return (
    <Box sx={containerSx}>
      <MDXEditor
        ref={mdxEditorRef}
        markdown={value}
        onChange={handleChange}
        contentEditableClassName='mdxContent'
        plugins={[
          toolbarPlugin({
            toolbarContents: () => (
              <RowStack justifyContent={'space-between'}>
                <RowStack
                  spacing={8}
                  sx={{
                    '& div': {
                      gap: 8,
                      button: {
                        borderRadius: 1
                      }
                    }
                  }}>
                  <BoldItalicUnderlineToggles options={['Bold', 'Italic']} />
                  <ListsToggle options={['check', 'bullet', 'number']} />
                </RowStack>

                {!hideCopy && (
                  <ButtonWithTooltip
                    style={{
                      pointerEvents: value ? 'all' : 'none'
                    }}
                    onClick={handleCopy}
                    title={'Copy'}>
                    <ContentCopyIcon
                      style={{
                        color: value
                          ? theme.palette.primary.main
                          : colors.Grey350
                      }}
                    />
                  </ButtonWithTooltip>
                )}
              </RowStack>
            )
          }),
          listsPlugin()
        ]}
        iconComponentFor={icon => {
          if (customFormatIcons[icon]) {
            return customFormatIcons[icon]
          }
          return defaultSvgIcons[icon]
        }}
        readOnly={readOnly}
        {...otherProps}
      />
    </Box>
  )
}

export default MdxEditor
